import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultDateTimeFormat, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import dayjs from 'dayjs';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import ContractConsentRequestType from 'models/enums/ContractConsentRequestType';
import contractConsentManualAcceptancedApi from 'models/sales/contractConsent/manualAcceptance/Api.ContractConsent.ManualAcceptance';
import ContractConsent from 'models/sales/contractConsent/Model.ContractConsent';
import contractConsentResendApi from 'models/sales/contractConsent/resend/Api.ContractConsent.Resend';
import ContractConsentAudit from 'models/sales/contractConsentAudit/Model.ContractConsentAudit';
import { useEffect, useMemo } from 'react';
import { contractConsentIndexRoute } from './ContractConsent.Index';

export enum ContractConsentAction {
  VIEW = 'view',
  RESEND_CONSENT = 'resendConsent',
  INFO = 'info',
  MANUALLY_ACCEPT_CONSENT = 'manuallyAcceptConsent',
}

export enum ContractConsentAcceptanceStatus {
  YES = 'YES',
  NO = 'NO',
  YES_MANUAL = 'YES - MANUAL',
  NA = 'N/A',
}

export enum ContractConsentBackendAcceptanceState {
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  PENDING = 'PENDING',
}

export default function useContractConsent() {
  const { lang } = getLocales();
  const { user, permissions } = useAuthStore();
  const pageRouter = usePageRouter<Module.Sales.ContractConsent.Params, Module.Sales.ContractConsent.Query>({
    route: contractConsentIndexRoute,
  });

  const canView = permissions.RESEND_CONTRACTCONSENT || permissions.MANUALLY_ACCEPT_CONTRACTCONSENT;

  //#region APIS
  const listApi = useApi({
    action: ContractConsent.list,
    default: {
      limit: defaultLimit,
      offset: defaultOffset,
      distributorId: user?.distributorId ? [user.distributorId] : undefined,
      dealerId: user?.dealers?.map((it) => it.id) ?? [],
    },
    body: {
      ...pageRouter.query.list,
    },
    wait: !canView,
    callback: (req) => {
      pageRouter.redirect({}, { ...pageRouter.query, list: req });
    },
  });

  const dealersApi = useApi({
    action: Dealer.list,
    body: {
      distributorId: user?.distributorId ? [user.distributorId] : undefined,
      id: user?.dealers?.map((it) => it.id) ?? [],
    },
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const contractConsentAuditListApi = useApi({
    action: ContractConsentAudit.list,
    wait: true,
  });
  useEffect(() => {
    if (selected?.id) {
      contractConsentAuditListApi.execute({
        requestContractConsentId: [selected?.id],
        contractConsentId: [selected?.id],
      });
    } else {
      contractConsentAuditListApi.reset(true);
    }
  }, [selected?.id]);
  //#endregion

  const isEnabled = (consent?: ContractConsent) => {
    return (
      !!consent &&
      getAcceptedStatus(consent) === ContractConsentAcceptanceStatus.NA &&
      (permissions.RESEND_CONTRACTCONSENT || permissions.MANUALLY_ACCEPT_CONTRACTCONSENT)
    );
  };

  //#region EMAIL RESEND
  const resendConsent = async (params: {
    id: number;
    requestType: Model.Enum.ContractConsentRequestType;
    customerEmail?: string;
    customerPhoneNumber?: string;
  }) => {
    if (!selected || !isEnabled(selected)) {
      pageRouter.updateParams({ id: selected?.id?.toString(), action: ContractConsentAction.VIEW });
      return;
    }

    const res = await contractConsentResendApi(
      { id: params.id, requestType: params.requestType },
      { customerEmail: params.customerEmail, customerPhoneNumber: params.customerPhoneNumber }
    );

    if (res.status !== HttpStatus.ok) {
      addLog({ error: res.message });
      return;
    }

    addLog({ success: lang.saveSuccess });

    pageRouter.updateParams({ id: String(params.id), action: ContractConsentAction.VIEW });

    listApi.execute((body) => body);
  };

  const manuallyAccept = async (params: {
    id: number;
    reasonForManualAcceptance: string;
    noteForManualAcceptance?: string;
  }) => {
    if (!selected || !isEnabled(selected)) {
      pageRouter.updateParams({ id: selected?.id?.toString(), action: ContractConsentAction.VIEW });
      return;
    }

    const res = await contractConsentManualAcceptancedApi(
      { id: params.id },
      {
        reasonForManualAcceptance: params.reasonForManualAcceptance,
        noteForManualAcceptance: params.noteForManualAcceptance,
      }
    );

    if (res.status !== HttpStatus.ok) {
      addLog({ error: res.message });
      return;
    }

    addLog({ success: lang.saveSuccess });

    pageRouter.updateParams({ id: String(params.id), action: ContractConsentAction.VIEW });

    listApi.execute((body) => body);
  };
  //#endregion

  //#region METHODS

  const getTokenStatus = (consent: ContractConsent): string | 'EXPIRED' | 'N/A' => {
    if (consent?.isAccepted !== null || !consent?.modifiedAtUtc) {
      return 'N/A';
    }

    const modified = dayjs(consent?.modifiedAtUtc, defaultServerDateTimeFormat);
    const expiresAt = modified.add(7, 'day');

    return dayjs().isBefore(expiresAt) ? expiresAt.format(defaultDateTimeFormat) : 'EXPIRED';
  };

  const getAcceptedStatus = (
    consent: ContractConsent
  ):
    | ContractConsentAcceptanceStatus.YES
    | ContractConsentAcceptanceStatus.NO
    | ContractConsentAcceptanceStatus.NA
    | ContractConsentAcceptanceStatus.YES_MANUAL => {
    if (consent.isAccepted === true && consent.acceptedByUserId !== null && consent.acceptedByUserId !== undefined) {
      return ContractConsentAcceptanceStatus.YES_MANUAL;
    }
    if ((consent.isAccepted === true && consent.acceptedByUserId === null) || consent.acceptedByUserId === undefined)
      return ContractConsentAcceptanceStatus.YES;
    if (consent.isAccepted === false) return ContractConsentAcceptanceStatus.NO;
    return ContractConsentAcceptanceStatus.NA;
  };

  const acceptanceStatusItems: Component.Input.Select.SelectionItem[] = [
    { id: ContractConsentAcceptanceStatus.YES, title: ContractConsentAcceptanceStatus.YES },
    { id: ContractConsentAcceptanceStatus.YES_MANUAL, title: ContractConsentAcceptanceStatus.YES_MANUAL },
    { id: ContractConsentAcceptanceStatus.NO, title: ContractConsentAcceptanceStatus.NO },
    { id: ContractConsentAcceptanceStatus.NA, title: ContractConsentAcceptanceStatus.NA },
  ];

  const typeItems: Component.Input.Select.SelectionItem[] = [
    { id: ContractConsentRequestType.EMAIL, title: ContractConsentRequestType.EMAIL },
    { id: ContractConsentRequestType.TEXT, title: ContractConsentRequestType.TEXT },
  ];

  const buildAcceptanceStatusFilter = (
    ui?: ContractConsentAcceptanceStatus
  ): Partial<Pick<Api.Core.ContractConsent.List.IRequest, 'acceptanceState' | 'isManuallyAccepted'>> | undefined => {
    if (!ui) return undefined;

    switch (ui) {
      case ContractConsentAcceptanceStatus.YES:
        return {
          acceptanceState: ContractConsentBackendAcceptanceState.ACCEPTED,
          isManuallyAccepted: false,
        };
      case ContractConsentAcceptanceStatus.YES_MANUAL:
        return {
          acceptanceState: ContractConsentBackendAcceptanceState.ACCEPTED,
          isManuallyAccepted: true,
        };
      case ContractConsentAcceptanceStatus.NO:
        return {
          acceptanceState: ContractConsentBackendAcceptanceState.REJECTED,
          isManuallyAccepted: false,
        };
      case ContractConsentAcceptanceStatus.NA:
        return {
          acceptanceState: ContractConsentBackendAcceptanceState.PENDING,
          isManuallyAccepted: false,
        };
      default:
        return undefined;
    }
  };

  //#endregion

  return {
    pageRouter,
    listApi,
    contractConsentAuditListApi,
    dealersApi,
    canView,
    selected,
    isEnabled,
    manuallyAccept,
    resendConsent,
    getTokenStatus,
    getAcceptedStatus,
    buildAcceptanceStatusFilter,
    acceptanceStatusItems,
    ContractConsentAcceptanceStatus,
    typeItems,
    views: {
      [ContractConsentAction.INFO]: pageRouter.params.action === ContractConsentAction.INFO && !!pageRouter.params.id,
      [ContractConsentAction.RESEND_CONSENT]:
        pageRouter.params.action === ContractConsentAction.RESEND_CONSENT &&
        !!pageRouter.params.id &&
        permissions.RESEND_CONTRACTCONSENT,
      [ContractConsentAction.MANUALLY_ACCEPT_CONSENT]:
        pageRouter.params.action === ContractConsentAction.MANUALLY_ACCEPT_CONSENT &&
        !!pageRouter.params.id &&
        permissions.MANUALLY_ACCEPT_CONTRACTCONSENT,
    },
  };
}

export function useContractConsentProvider() {
  return useDataProvider<ReturnType<typeof useContractConsent>>();
}
